//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';
import React from 'react';
import styled from 'styled-components';

import { PurchaseOption } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { SortDir } from '@apis/campaign-requests/shared/Sorting';
import { PrepaidPurchaseOptionsTableRow } from '@campaigns/components/shared/prepaid-purchase-options/prepaid-purchase-options-table-row/PrepaidPurchaseOptionsTableRow';
import {
  usePurchaseOptionsContext,
} from '@campaigns/components/shared/purchase-options/PurchaseOptionsContext';
import {
  PurchaseOptionsSortKey,
  WIDTH_CHECKBOX,
  WIDTH_EXPIRATION_DATE,
} from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { InvisibleButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { BodyText, TableHeaderCellText } from '@components/atoms/text/Text';
import VisuallyHiddenText from '@components/atoms/visually-hidden-text/VisuallyHiddenText';
import { Table } from '@components/organisms/table/Table';
import { RadioGroup } from '@components/primitives/radio-group/RadioGroup';

const TABLE_CAPTION_ID = 'sampling-campaigns-purchase-options-table-caption';

interface PrepaidPurchaseOptionsTableProps {
  onSelectionChange?: (optionId: string) => void;
  onSortChange: (sortKey: PurchaseOptionsSortKey) => void;
  purchaseOptions: PurchaseOption[];
  selectedOptionId?: string;
  sortDir: SortDir;
  sortKey: PurchaseOptionsSortKey;
}

export const PrepaidPurchaseOptionsTable = ({
  onSelectionChange,
  onSortChange,
  purchaseOptions,
  selectedOptionId,
  sortDir,
  sortKey,
}: PrepaidPurchaseOptionsTableProps) => {
  const { readOnly } = usePurchaseOptionsContext();

  if (purchaseOptions.length === 0) {
    return (
      <EmptyText>
        No options matched your search criteria. Please refine your search and
        try again.
      </EmptyText>
    );
  }

  const iconName: UnisonIconType =
    sortDir === SortDir.ASC ? 'chevron-up' : 'chevron-down';

  const headerCells = [
    <TableHeaderCellText key='purchase' justifyContent='flex-start'>
      PURCHASE
    </TableHeaderCellText>,
    <TableHeaderCellText key='content' justifyContent='flex-start'>
      CONTENT
    </TableHeaderCellText>,
    <TableHeaderCellText key='deliveryMethod' justifyContent='flex-start'>
      DELIVERY METHOD
    </TableHeaderCellText>,
    <TableHeaderCellText key='usage' justifyContent='flex-start'>
      USAGE
    </TableHeaderCellText>,
    <InvisibleButton
      key='expiration'
      onClick={() => {
        onSortChange(PurchaseOptionsSortKey.EXPIRY_DATE);
      }}
      trackingId={
        SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_PURCHASE_OPTIONS_SORT_BY_EXPIRATION_DATE_BUTTON
      }
    >
      <ExpirationDateCell justifyContent='flex-start'>
        EXPIRY
        <VisuallyHiddenText>
          Sort by expiry&nbsp;
          {sortDir === SortDir.ASC ? 'ascending' : 'descending'}
        </VisuallyHiddenText>
        {sortKey === PurchaseOptionsSortKey.EXPIRY_DATE && (
          <Icon name={iconName} />
        )}
      </ExpirationDateCell>
    </InvisibleButton>,
  ];

  if (!readOnly) {
    headerCells.unshift(
      <CheckboxCell key='selected' justifyContent='flex-start' />,
    );
  }

  return (
    <RadioGroup onValueChange={onSelectionChange} value={selectedOptionId}>
      <Table
        body={purchaseOptions.map((purchaseOption) => (
          <PrepaidPurchaseOptionsTableRow
            key={purchaseOption.opportunityId}
            purchaseOption={purchaseOption}
          />
        ))}
        caption={<VisuallyHiddenText>Purchase Options</VisuallyHiddenText>}
        captionId={TABLE_CAPTION_ID}
        headerCells={headerCells}
      />
    </RadioGroup>
  );
};

const StyledTableHeaderCellText = styled(TableHeaderCellText)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const CheckboxCell = styled(TableHeaderCellText)`
  ${WIDTH_CHECKBOX}
`;

const ExpirationDateCell = styled(StyledTableHeaderCellText)`
  ${WIDTH_EXPIRATION_DATE}
`;

const EmptyText = styled(BodyText)(
  ({ theme }) => `
    margin-top: ${theme.spacings[2]};
  `,
);
